import { useCallback } from "react";
import {
  useCancelAiMappingTask,
  useDetectAiMappingRunArea,
  useExportAiMappingRunAreaPdf,
  useGetAiMappingRunArea,
  useGetAiMappingRunAreaResults,
  useGetAiMappingTaskStatus,
  useSegmentAiMappingRunArea,
} from "./hooks";
import { runAiMappingDetectFlow } from "./aiMappingDetectFlow";
import { classifyAiMappingError } from "./aiMappingErrors";
import {
  AiMappingActiveTask,
  AiMappingNotify,
  AiMappingTaskKind,
} from "./aiMappingControllerTypes";
import { TaskPayloadLike, unwrap } from "./aiMappingControllerUtils";
import { AiMappingAreaLike, AiMappingResultsLike } from "./aiMappingTypes";
import { AiMappingTaskSnapshot, initialAiMappingTaskSnapshot } from "./aiMappingTask";

interface UseAiMappingAreaTasksOptions {
  activeTask: AiMappingActiveTask | null;
  pollIntervalMs?: number;
  unmountedRef: React.MutableRefObject<boolean>;
  detectRunIdRef: React.MutableRefObject<number>;
  setActiveTask: React.Dispatch<React.SetStateAction<AiMappingActiveTask | null>>;
  setTaskSnapshot: React.Dispatch<React.SetStateAction<AiMappingTaskSnapshot | null>>;
  updateAreaInRun: (area: AiMappingAreaLike) => void;
  applyResults: (payload: AiMappingResultsLike) => void;
  refreshRun: () => Promise<void>;
  getAreaById: (areaId: string) => AiMappingAreaLike | null;
  sleep: (ms: number) => Promise<void>;
  cancelSleep: () => void;
  notify: AiMappingNotify;
  handleError: (error: unknown, options?: { inline?: boolean }) => boolean;
}

export function useAiMappingAreaTasks({
  activeTask,
  pollIntervalMs,
  unmountedRef,
  detectRunIdRef,
  setActiveTask,
  setTaskSnapshot,
  updateAreaInRun,
  applyResults,
  refreshRun,
  getAreaById,
  sleep,
  cancelSleep,
  notify,
  handleError,
}: UseAiMappingAreaTasksOptions) {
  const [detectAction] = useDetectAiMappingRunArea();
  const [segmentAction] = useSegmentAiMappingRunArea();
  const [exportPdfAction] = useExportAiMappingRunAreaPdf();
  const [cancelTaskAction] = useCancelAiMappingTask();
  const [fetchTaskStatus] = useGetAiMappingTaskStatus();
  const [fetchResultsQuery] = useGetAiMappingRunAreaResults();
  const [fetchArea] = useGetAiMappingRunArea();

  const runAreaTask = useCallback(
    async (
      areaId: string,
      kind: AiMappingTaskKind,
      startTask: (id: string) => Promise<TaskPayloadLike>,
      successMessage: string
    ) => {
      if (activeTask) return;
      const runId = (detectRunIdRef.current += 1);
      const isStale = () => unmountedRef.current || detectRunIdRef.current !== runId;

      setActiveTask({ areaId, kind });
      setTaskSnapshot(initialAiMappingTaskSnapshot);
      try {
        const outcome = await runAiMappingDetectFlow<AiMappingResultsLike>(areaId, {
          pollIntervalMs,
          sleep,
          isCancelled: isStale,
          onSnapshot: (snapshot) => {
            if (!isStale()) setTaskSnapshot(snapshot);
          },
          startTask: async (id) => {
            const payload = await startTask(id);
            if (!isStale() && payload.area) updateAreaInRun(payload.area);
            return { taskId: payload.taskId };
          },
          fetchTaskStatus: async (taskId) =>
            unwrap(
              await fetchTaskStatus({
                variables: { taskId },
                fetchPolicy: "network-only",
              }),
              (data) => data?.getAiMappingTaskStatus
            ),
          fetchResults:
            kind === "pdf"
              ? undefined
              : async (id) =>
                  unwrap(
                    await fetchResultsQuery({
                      variables: { id },
                      fetchPolicy: "network-only",
                    }),
                    (data) => data?.getAiMappingRunAreaResults
                  ) as Promise<AiMappingResultsLike>,
        });

        if (isStale()) return;

        if (outcome.kind === "success") {
          if (kind === "pdf") {
            if (outcome.pdfUrl) {
              window.open(outcome.pdfUrl, "_blank");
            }
            notify(successMessage, "success");
          } else if (outcome.results) {
            applyResults(outcome.results);
            notify(successMessage, "success");
          }
        } else if (outcome.kind === "failure") {
          notify(outcome.error, "error");
        }
      } catch (error) {
        if (isStale()) return;
        const info = classifyAiMappingError(error);
        if (info.code === "task_not_found") {
          notify(info.message, "error");
          try {
            const fresh = unwrap(
              await fetchArea({ variables: { id: areaId }, fetchPolicy: "network-only" }),
              (data) => data?.getAiMappingRunArea
            );
            if (!isStale()) updateAreaInRun(fresh as AiMappingAreaLike);
          } catch (refetchError) {
            if (!isStale()) handleError(refetchError);
          }
        } else if (info.code === "area_not_found" || info.code === "run_not_found") {
          notify(info.message, "error");
          await refreshRun();
        } else {
          handleError(error);
        }
      } finally {
        if (!isStale()) {
          setActiveTask(null);
          setTaskSnapshot(null);
        }
      }
    },
    [
      activeTask,
      pollIntervalMs,
      sleep,
      unmountedRef,
      detectRunIdRef,
      setActiveTask,
      setTaskSnapshot,
      fetchTaskStatus,
      fetchResultsQuery,
      fetchArea,
      updateAreaInRun,
      applyResults,
      refreshRun,
      handleError,
      notify,
    ]
  );

  const detectArea = useCallback(
    async (areaId: string) => {
      await runAreaTask(
        areaId,
        "detect",
        async (id) => {
          const result = await detectAction({ variables: { id } });
          const payload = result.data?.detectAiMappingRunArea;
          if (!result.success || !payload) {
            throw result.success ? new Error("ai_mapping_unavailable") : (result as unknown);
          }
          return payload as TaskPayloadLike;
        },
        "Detection complete"
      );
    },
    [runAreaTask, detectAction]
  );

  const segmentArea = useCallback(
    async (areaId: string) => {
      await runAreaTask(
        areaId,
        "segment",
        async (id) => {
          const result = await segmentAction({ variables: { id } });
          const payload = result.data?.segmentAiMappingRunArea;
          if (!result.success || !payload) {
            throw result.success ? new Error("ai_mapping_unavailable") : (result as unknown);
          }
          return payload as TaskPayloadLike;
        },
        "Segmentation complete"
      );
    },
    [runAreaTask, segmentAction]
  );

  const exportPdf = useCallback(
    async (areaId: string) => {
      await runAreaTask(
        areaId,
        "pdf",
        async (id) => {
          const result = await exportPdfAction({ variables: { id } });
          const payload = result.data?.exportAiMappingRunAreaPdf;
          if (!result.success || !payload) {
            throw result.success ? new Error("ai_mapping_unavailable") : (result as unknown);
          }
          return payload as TaskPayloadLike;
        },
        "Report downloaded"
      );
    },
    [runAreaTask, exportPdfAction]
  );

  const cancelTask = useCallback(async () => {
    const task = activeTask;
    if (!task) return;
    detectRunIdRef.current += 1;
    cancelSleep();
    setActiveTask(null);
    setTaskSnapshot(null);

    const area = getAreaById(task.areaId);
    const taskId = area?.taskId;
    if (!taskId) return;
    try {
      const result = await cancelTaskAction({ variables: { taskId } });
      const refreshed = result.data?.cancelAiMappingTask as AiMappingAreaLike | undefined;
      if (refreshed && !unmountedRef.current) {
        updateAreaInRun(refreshed);
      }
      notify("Task cancelled", "info");
    } catch (error) {
      if (!unmountedRef.current) handleError(error);
    }
  }, [
    activeTask,
    getAreaById,
    cancelTaskAction,
    updateAreaInRun,
    notify,
    handleError,
    cancelSleep,
    detectRunIdRef,
    setActiveTask,
    setTaskSnapshot,
    unmountedRef,
  ]);

  return { detectArea, segmentArea, exportPdf, cancelTask };
}
